// ─────────────────────────────────────────────────────────────────────────────
// MCA entity framework — single source of truth for how the Company Master,
// Registers, Annual Filings and Events Master modules should behave for a
// given entity type.
//
// A "company" (Private/Public/OPC/Section 8/Producer/Nidhi) is a Companies
// Act, 2013 entity. An "LLP" is a Limited Liability Partnership Act, 2008
// entity — a different legal regime with different parties (partners, not
// directors), different capital (contribution, not shares), different
// meetings (non-statutory) and different filings (Form 11 / Form 8, not
// AOC-4 / MGT-7). Every consumer reads its labels/rules from here so the
// terminology and compliance logic stay consistent.
// ─────────────────────────────────────────────────────────────────────────────

export const isLLPType = (companyType) =>
  /\b(llp|limited liability partnership)\b/i.test(String(companyType || ''));

const isPublicType = (companyType) => /public/i.test(String(companyType || ''));
const isSmallOrOpcType = (companyType) => {
  const t = String(companyType || '').toLowerCase();
  return t.includes('one person') || t.includes('opc') || t.includes('small');
};

// ── LLP profile ──────────────────────────────────────────────────────────────
const LLP_PROFILE = Object.freeze({
  isLLP: true,
  kind: 'LLP',
  law: 'Limited Liability Partnership Act, 2008',
  lawShort: 'LLP Act, 2008',

  identifier: { label: 'LLPIN', field: 'cin_llpin' },

  party: {
    one: 'Partner',
    many: 'Partners',
    roster: 'Partners & Designated Partners',
    designated: 'Designated Partner',
    designatedShort: 'DP',
    pin: 'DIN / DPIN',
    pinFull: 'DIN / DPIN (Designated Partner Identification Number)',
    residentNote: 'Minimum 2 partners, at least 2 Designated Partners; at least one DP must be resident in India.',
    addLabel: 'Add Partner',
    emptyText: 'No partners added yet',
  },

  capital: {
    label: 'Contribution',
    obligation: 'Obligation to Contribute',
    received: 'Contribution Received',
    profitShare: 'Profit-Sharing Ratio',
    totalLabel: 'Total Contribution (as per LLP Agreement)',
    formsOf: ['Cash', 'Bank transfer', 'Tangible property', 'Intangible property', 'Other benefit'],
    registerTitle: 'Contribution Register',
    registerNote: 'Per the LLP Agreement · disclosed in Form 11 & Form 8',
  },

  forms: {
    appointment: 'Form 4',                       // vs DIR-12
    appointmentFull: 'Form 4 (Notice of appointment / cessation / change of partner)',
    incorporation: 'FiLLiP',                     // vs SPICe+ / INC-32
    agreement: 'LLP Agreement',                  // vs MOA & AOA
    agreementFiling: 'Form 3',                   // LLP Agreement & every change
    kyc: 'DIR-3 KYC',
    kycLabel: 'DPIN KYC (DIR-3 KYC)',
    kycDue: '30 September',
    // DIR-3 KYC became once-every-3-FYs for every DIN/DPIN holder under the
    // Companies (Registration Offices & Fees) Amendment Rules, 2026.
    kycCadence: 'triennial',
  },

  meetings: {
    title: "Partners' Meetings & Decisions",
    statutory: false,
    note: 'Governed by the LLP Agreement — not a statutory requirement under the LLP Act, 2008.',
    types: ["Partners' Meeting", 'Meeting by Circulation', 'Adjourned Meeting'],
    decisionModes: ['By majority of partners', 'Unanimous', 'As per LLP Agreement'],
    // Partners' Meetings live in the shared mca_meetings table (the LLP
    // Register owns them), NOT the company mca_board_meetings table.
    store: 'mca_meetings',
    meetingTypeKey: 'PARTNERS',
    circulationTypeKey: 'PARTNERS_CIRCULATION',
  },

  cessationReasons: ['Resignation', 'Retirement (per Agreement)', 'Death', 'Insolvency', 'Expulsion', 'Dissolution'],
  partnerTypes: ['Individual', 'Body Corporate', 'Nominee of Body Corporate'],

  // Tabs the Company Master detail view should render (in order).
  tabs: ['overview', 'directors', 'shareholders', 'meetings', 'charges', 'filings'],

  statCards: { kycLabel: 'DPIN KYC Pending', lastLabel: 'Annual Return (Form 11)' },

  // Annual ROC filings for an LLP. FY always ends 31 March.
  annualForms: [
    { form: 'Form 11', label: 'Annual Return', dueMMDD: '05-30', basis: 'llp-may30', mandatory: true },
    { form: 'Form 8', label: 'Statement of Account & Solvency', dueMMDD: '10-30', basis: 'llp-oct30', mandatory: true },
  ],

  audit: {
    turnover: 4000000,
    contribution: 2500000,
    rule: 'Rule 24(8) — accounts audit required if turnover exceeds ₹40 lakh or contribution exceeds ₹25 lakh.',
  },

  // Event-based ROC forms available for an LLP (Events Master picker).
  eventForms: [
    'FiLLiP (Incorporation of LLP)',
    'RUN-LLP (Reserve Unique Name)',
    'Form 3 (LLP Agreement & changes)',
    'Form 4 (Partner appointment / cessation / change)',
    'Form 5 (Notice for change of name)',
    'Form 8 (Statement of Account & Solvency)',
    'Form 9 (Consent to act as Designated Partner)',
    'Form 11 (Annual Return)',
    'Form 12 (Address for service of documents)',
    'Form 15 (Change of registered office)',
    'Form 22 (Notice of intimation of Order)',
    'Form 23 (Application to RD to direct change of name)',
    'Form 24 (Application for striking off name)',
    'Form 25 (Reservation / renewal of name by Foreign LLP)',
    'Form 27 (Registration of particulars by Foreign LLP)',
    'Form 28 (Alteration in incorporation document of Foreign LLP)',
    'Form 31 (Application for compounding of an offence)',
    'Form 32 (Addendum for rectification of defects)',
    'CHG-1 (Creation / modification of charge)',
    'CHG-4 (Satisfaction of charge)',
  ],
});

// ── Company (Companies Act 2013) profile ─────────────────────────────────────
const companyProfile = (companyType) =>
  Object.freeze({
    isLLP: false,
    kind: 'COMPANY',
    law: 'Companies Act, 2013',
    lawShort: 'Companies Act, 2013',
    isPublic: isPublicType(companyType),
    isSmallOrOpc: isSmallOrOpcType(companyType),

    identifier: { label: 'CIN', field: 'cin_llpin' },

    party: {
      one: 'Director',
      many: 'Directors',
      roster: 'Board of Directors',
      designated: 'Managing Director',
      designatedShort: 'MD',
      pin: 'DIN',
      pinFull: 'DIN (Director Identification Number)',
      residentNote: 'At least one director must be resident in India (Sec 149(3)).',
      addLabel: 'Add Director',
      emptyText: 'No directors added yet',
    },

    capital: {
      label: 'Share Capital',
      obligation: 'Authorized Capital',
      received: 'Paid-up Capital',
      profitShare: 'Shareholding',
      totalLabel: 'Paid-up Capital',
      formsOf: [],
      registerTitle: 'Register of Members',
      registerNote: 'Statutory format (MGT-1) as per Section 88 of the Companies Act, 2013',
    },

    forms: {
      appointment: 'DIR-12',
      appointmentFull: 'DIR-12 (Sec 170(2) / Rule 18)',
      incorporation: 'SPICe+ / INC-32',
      agreement: 'MOA & AOA',
      agreementFiling: 'MGT-14',
      kyc: 'DIR-3 KYC',
      kycLabel: 'DIN KYC (DIR-3 KYC)',
      kycDue: '30 September',
      kycCadence: 'triennial',
    },

    meetings: {
      title: 'Board Meeting Register',
      statutory: true,
      note: 'As per SS-1 and Section 118 of the Companies Act, 2013.',
      types: ['Board Meeting', 'Adjourned Board Meeting', 'Circular Resolution'],
      decisionModes: ['Ordinary Resolution', 'Special Resolution', 'Board Resolution'],
    },

    cessationReasons: ['Resignation', 'Removal', 'Death', 'Disqualification', 'Vacation of Office'],
    partnerTypes: [],

    tabs: [
      'overview', 'directors', 'auditors', 'shareholders', 'transfers', 'meetings',
      'general-meetings', 'committees', 'charges', 'contracts', 'filings',
    ],

    statCards: { kycLabel: 'DIN KYC Pending', lastLabel: 'Last AGM' },

    annualForms: [], // computed by AnnualFilings / annualFilingRoutes

    audit: null,
    eventForms: [],
  });

/**
 * Returns the entity profile for a company_type string.
 * @param {string} companyType e.g. "LLP", "Private Limited Company"
 */
export function getEntityProfile(companyType) {
  return isLLPType(companyType) ? LLP_PROFILE : companyProfile(companyType);
}

export { LLP_PROFILE };
